import {COMPACT_TABLES_CLASS} from '@github-ui/copilot-chat/components/chat-message-table-class'
import {MarkdownRenderer, type MarkdownRendererProps} from '@github-ui/copilot-markdown'
import {clsx} from 'clsx'
import React, {useCallback, useMemo, useRef} from 'react'

import {ContentPreviewBlockContext} from '../markdown-extensions/ContentPreviewBlockContext'
import fileViewExtension from '../markdown-extensions/file-blocks/file-blocks'
import draftIssueBlockExtension from '../markdown-extensions/issue-blocks/draft-issue/extension'
import draftIssueTreeExtension from '../markdown-extensions/issue-blocks/draft-issue-tree/extension'
import issueLinkBlockExtension from '../markdown-extensions/issue-blocks/issue-link/extension'
import issueListBlockExtension from '../markdown-extensions/issue-blocks/issue-list/extension'
import issueTreeExtension from '../markdown-extensions/issue-blocks/issue-tree/extension'
import styles from './MarkdownViewer.module.css'

interface MarkdownViewerProps
  extends
    Omit<MarkdownRendererProps, 'onOpenFile'>,
    Omit<ContentPreviewBlockContext, 'hasAutoOpenedPreviewPaneRef' | 'setHasAutoOpenedPreviewPane'> {
  onGitHubLinkClick?: (event: MouseEvent | React.MouseEvent<HTMLElement>) => void
  // Opts the rendered markdown into the chat compact-table treatment. Caller is
  // responsible for combining this with any feature-flag gate; MarkdownViewer
  // itself does not read flags so non-chat consumers (timeline events) stay opted
  // out by default.
  compactTables?: boolean
}

export const MarkdownViewer = ({
  messageId,
  messageIndex,
  messageTimestamp,
  autoOpenPreviewPane,
  markdown,
  references,
  onGitHubLinkClick,
  compactTables,
  // Defaults to "immersive" for non-chat callers (e.g. timeline events). Chat callers pass the
  // live conversation mode so suggestion clicks and code-block telemetry are tagged with the
  // actual surface (e.g. "assistive" when this immersive UI is embedded), matching the mode
  // reported by copilot.message_streaming_completed.
  chatMode = 'immersive',
  ...rendererProps
}: MarkdownViewerProps) => {
  const hasAutoOpenedPreviewPaneRef = useRef(false)
  const setHasAutoOpenedPreviewPane = useCallback((value: boolean) => {
    hasAutoOpenedPreviewPaneRef.current = value
  }, [])
  const extensions = useMemo(() => {
    const fileBlocksInMessage: string[] = []
    return [
      ...(rendererProps.extensions ?? []),
      fileViewExtension(fileBlocksInMessage),
      issueLinkBlockExtension(),
      issueListBlockExtension(),
      issueTreeExtension(),
      draftIssueBlockExtension(),
      draftIssueTreeExtension(),
    ]
  }, [rendererProps.extensions])

  return (
    <ContentPreviewBlockContext
      value={useMemo(
        () => ({
          messageId,
          messageIndex,
          messageTimestamp,
          autoOpenPreviewPane,
          markdown,
          references,
          hasAutoOpenedPreviewPaneRef,
          setHasAutoOpenedPreviewPane,
        }),
        [
          messageId,
          messageIndex,
          messageTimestamp,
          autoOpenPreviewPane,
          markdown,
          references,
          hasAutoOpenedPreviewPaneRef,
          setHasAutoOpenedPreviewPane,
        ],
      )}
    >
      <MarkdownRenderer
        {...rendererProps}
        markdown={markdown}
        extensions={extensions}
        chatMode={chatMode}
        className={clsx(styles.markdownOverrides, compactTables && COMPACT_TABLES_CLASS)}
        onLinkClick={onGitHubLinkClick}
      />
    </ContentPreviewBlockContext>
  )
}

export const MemoizedMarkdownViewer = React.memo(MarkdownViewer)
